/**
 * @file Shared constants for the calendar library.
 * These constants are safe to use in both client and server environments.
 */

/**
 * Default currency for financial calculations.
 */
export const DEFAULT_CURRENCY = 'AUD';

/**
 * Maximum event duration in hours.
 */
export const MAX_EVENT_DURATION_HOURS = 24;

/**
 * Maximum number of recurrence occurrences.
 */
export const MAX_RECURRENCE_OCCURRENCES = 365;

/**
 * Default event duration in minutes.
 */
export const DEFAULT_EVENT_DURATION_MINUTES = 60;

/**
 * Maximum field lengths for validation.
 */
export const MAX_LENGTHS = {
  EVENT_TITLE: 200,
  EVENT_DESCRIPTION: 1000,
  CALENDAR_NAME: 100,
  CALENDAR_DESCRIPTION: 500,
  VENUE_NAME: 200,
  VENUE_ADDRESS: 300,
  VENUE_CITY: 100,
  CONTACT_NAME: 200,
  CONTACT_ROLE: 100,
  NOTES: 1000,
  INCOME_DESCRIPTION: 200,
  EXPENSE_DESCRIPTION: 200,
  RECEIPT_PATH: 500
} as const;

/**
 * Date format patterns for different locales.
 */
export const DATE_FORMATS = {
  AUSTRALIAN: 'DD/MM/YYYY',
  AMERICAN: 'MM/DD/YYYY',
  ISO: 'YYYY-MM-DD'
} as const;

/**
 * Time format patterns.
 */
export const TIME_FORMATS = {
  TWELVE_HOUR: '12h',
  TWENTY_FOUR_HOUR: '24h'
} as const;

/**
 * Default calendar view options.
 */
export const CALENDAR_VIEWS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  LIST: 'list'
} as const;

/**
 * Default start of week (0 = Sunday, 1 = Monday, etc.)
 */
export const DEFAULT_START_OF_WEEK = 1; // Monday

/**
 * Australian states and territories.
 */
export const AUSTRALIAN_STATES = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory'
} as const;

/**
 * Common currency codes.
 */
export const CURRENCIES = {
  AUD: 'Australian Dollar',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  CAD: 'Canadian Dollar',
  NZD: 'New Zealand Dollar'
} as const;

/**
 * Validation patterns.
 */
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_AU: /^(\+61|0)[2-478](?:[ -]?[0-9]){8}$/,
  CURRENCY_CODE: /^[A-Z]{3}$/,
  HEX_COLOR: /^#[0-9A-F]{6}$/i,
  URL: /^https?:\/\/.+/
} as const;

/**
 * API configuration constants.
 */
export const API_CONFIG = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  REQUEST_TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3
} as const;

/**
 * File upload constraints.
 */
export const FILE_UPLOAD = {
  MAX_SIZE_MB: 10,
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf']
} as const; 